import { useEffect, useRef } from 'react';
import {
  Camera,
  ClampToEdgeWrapping,
  HalfFloatType,
  LinearFilter,
  Mesh,
  OrthographicCamera,
  PlaneGeometry,
  RGBAFormat,
  Scene,
  ShaderMaterial,
  Vector2,
  Vector3,
  WebGLRenderTarget,
  WebGLRenderer,
} from 'three';

type PointerState = {
  x: number;
  y: number;
  dx: number;
  dy: number;
  sdx: number;
  sdy: number;
  moved: boolean;
  active: boolean;
  pointerType: 'mouse' | 'touch' | 'pen';
  pointerId: number | null;
};

type DoubleFBO = {
  read: WebGLRenderTarget;
  write: WebGLRenderTarget;
  swap: () => void;
};

export type FluidSettings = {
  mouseForce: number;
  cursorSize: number;
  isViscous: boolean;
  viscous: number;
  iterationsViscous: number;
  iterationsPoisson: number;
  dt: number;
  velocityDissipation: number;
  dyeDissipation: number;
};

export const FLUID_PRESETS: Record<string, FluidSettings> = {
  balanced: {
    mouseForce: 22,
    cursorSize: 60,
    isViscous: true,
    viscous: 28,
    iterationsViscous: 12,
    iterationsPoisson: 20,
    dt: 0.011,
    velocityDissipation: 0.994,
    dyeDissipation: 0.992,
  },
  calm: {
    mouseForce: 18,
    cursorSize: 66,
    isViscous: true,
    viscous: 34,
    iterationsViscous: 16,
    iterationsPoisson: 18,
    dt: 0.01,
    velocityDissipation: 0.995,
    dyeDissipation: 0.993,
  },
  punchy: {
    mouseForce: 28,
    cursorSize: 54,
    isViscous: true,
    viscous: 22,
    iterationsViscous: 10,
    iterationsPoisson: 24,
    dt: 0.013,
    velocityDissipation: 0.992,
    dyeDissipation: 0.989,
  },
};

export const DEFAULT_FLUID_SETTINGS: FluidSettings = FLUID_PRESETS.balanced;

const BASE_GRAY = 247 / 255;
const MID_GRAY = 220 / 255;
const INK_GRAY = 168 / 255;

const baseVertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

const advectionShader = `
  varying vec2 vUv;
  uniform sampler2D uSource;
  uniform sampler2D uVelocity;
  uniform vec2 uTexelSize;
  uniform float uDt;
  uniform float uDissipation;

  void main() {
    vec2 vel = texture2D(uVelocity, vUv).xy;
    vec2 coord = vUv - uDt * vel * uTexelSize;
    vec4 value = texture2D(uSource, coord);
    gl_FragColor = vec4(value.rgb * uDissipation, 1.0);
  }
`;

const divergenceShader = `
  varying vec2 vUv;
  uniform sampler2D uVelocity;
  uniform vec2 uTexelSize;

  void main() {
    float L = texture2D(uVelocity, vUv - vec2(uTexelSize.x, 0.0)).x;
    float R = texture2D(uVelocity, vUv + vec2(uTexelSize.x, 0.0)).x;
    float B = texture2D(uVelocity, vUv - vec2(0.0, uTexelSize.y)).y;
    float T = texture2D(uVelocity, vUv + vec2(0.0, uTexelSize.y)).y;
    float div = 0.5 * (R - L + T - B);
    gl_FragColor = vec4(div, 0.0, 0.0, 1.0);
  }
`;

const pressureShader = `
  varying vec2 vUv;
  uniform sampler2D uPressure;
  uniform sampler2D uDivergence;
  uniform vec2 uTexelSize;

  void main() {
    float L = texture2D(uPressure, vUv - vec2(uTexelSize.x, 0.0)).x;
    float R = texture2D(uPressure, vUv + vec2(uTexelSize.x, 0.0)).x;
    float B = texture2D(uPressure, vUv - vec2(0.0, uTexelSize.y)).x;
    float T = texture2D(uPressure, vUv + vec2(0.0, uTexelSize.y)).x;
    float divergence = texture2D(uDivergence, vUv).x;
    float pressure = (L + R + B + T - divergence) * 0.25;
    gl_FragColor = vec4(pressure, 0.0, 0.0, 1.0);
  }
`;

const viscosityShader = `
  varying vec2 vUv;
  uniform sampler2D uVelocity;
  uniform vec2 uTexelSize;
  uniform float uAlpha;
  uniform float uBeta;

  void main() {
    vec2 L = texture2D(uVelocity, vUv - vec2(uTexelSize.x, 0.0)).xy;
    vec2 R = texture2D(uVelocity, vUv + vec2(uTexelSize.x, 0.0)).xy;
    vec2 B = texture2D(uVelocity, vUv - vec2(0.0, uTexelSize.y)).xy;
    vec2 T = texture2D(uVelocity, vUv + vec2(0.0, uTexelSize.y)).xy;
    vec2 C = texture2D(uVelocity, vUv).xy;
    vec2 result = (L + R + B + T + uAlpha * C) / uBeta;
    gl_FragColor = vec4(result, 0.0, 1.0);
  }
`;

const gradientSubtractShader = `
  varying vec2 vUv;
  uniform sampler2D uPressure;
  uniform sampler2D uVelocity;
  uniform vec2 uTexelSize;

  void main() {
    float L = texture2D(uPressure, vUv - vec2(uTexelSize.x, 0.0)).x;
    float R = texture2D(uPressure, vUv + vec2(uTexelSize.x, 0.0)).x;
    float B = texture2D(uPressure, vUv - vec2(0.0, uTexelSize.y)).x;
    float T = texture2D(uPressure, vUv + vec2(0.0, uTexelSize.y)).x;
    vec2 velocity = texture2D(uVelocity, vUv).xy;
    velocity -= vec2(R - L, T - B) * 0.5;
    gl_FragColor = vec4(velocity, 0.0, 1.0);
  }
`;

const curlShader = `
  varying vec2 vUv;
  uniform sampler2D uVelocity;
  uniform vec2 uTexelSize;

  void main() {
    float L = texture2D(uVelocity, vUv - vec2(uTexelSize.x, 0.0)).y;
    float R = texture2D(uVelocity, vUv + vec2(uTexelSize.x, 0.0)).y;
    float B = texture2D(uVelocity, vUv - vec2(0.0, uTexelSize.y)).x;
    float T = texture2D(uVelocity, vUv + vec2(0.0, uTexelSize.y)).x;
    float vorticity = R - L - (T - B);
    gl_FragColor = vec4(vorticity, 0.0, 0.0, 1.0);
  }
`;

const vorticityShader = `
  varying vec2 vUv;
  uniform sampler2D uVelocity;
  uniform sampler2D uCurl;
  uniform vec2 uTexelSize;
  uniform float uDt;
  uniform float uCurlStrength;

  void main() {
    float L = abs(texture2D(uCurl, vUv - vec2(uTexelSize.x, 0.0)).x);
    float R = abs(texture2D(uCurl, vUv + vec2(uTexelSize.x, 0.0)).x);
    float B = abs(texture2D(uCurl, vUv - vec2(0.0, uTexelSize.y)).x);
    float T = abs(texture2D(uCurl, vUv + vec2(0.0, uTexelSize.y)).x);
    float C = texture2D(uCurl, vUv).x;

    vec2 force = 0.5 * vec2(R - L, T - B);
    force /= (length(force) + 0.0001);
    force *= uCurlStrength * C;
    force.y *= -1.0;

    vec2 velocity = texture2D(uVelocity, vUv).xy;
    velocity += force * uDt;
    gl_FragColor = vec4(velocity, 0.0, 1.0);
  }
`;

const splatShader = `
  varying vec2 vUv;
  uniform sampler2D uTarget;
  uniform vec2 uPoint;
  uniform vec3 uColor;
  uniform vec2 uForce;
  uniform float uRadius;
  uniform float uIsVelocity;
  uniform float uAspect;

  void main() {
    vec2 p = vUv - uPoint;
    p.x *= uAspect;
    float falloff = exp(-dot(p, p) / uRadius);
    vec4 current = texture2D(uTarget, vUv);

    if (uIsVelocity > 0.5) {
      vec2 addVel = uForce * falloff;
      gl_FragColor = vec4(current.xy + addVel, 0.0, 1.0);
      return;
    }

    vec3 addColor = uColor * falloff;
    gl_FragColor = vec4(current.rgb + addColor, 1.0);
  }
`;

const displayShader = `
  varying vec2 vUv;
  uniform sampler2D uDye;
  uniform sampler2D uVelocity;
  uniform float uBaseGray;
  uniform float uMidGray;
  uniform float uInkGray;
  uniform vec2 uTexelSize;
  uniform float uTime;
  uniform float uPresetBreath;

  void main() {
    float density = texture2D(uDye, vUv).r;
    vec2 velocity = texture2D(uVelocity, vUv).xy;
    float speed = clamp(length(velocity) * 0.34, 0.0, 1.0);
    vec2 dx = vec2(uTexelSize.x, 0.0);
    vec2 dy = vec2(0.0, uTexelSize.y);

    float dL = texture2D(uDye, vUv - dx).r;
    float dR = texture2D(uDye, vUv + dx).r;
    float dB = texture2D(uDye, vUv - dy).r;
    float dT = texture2D(uDye, vUv + dy).r;

    float blur =
      dR + dL + dT + dB;

    // Soft accumulation for translucent layered look
    density = mix(density, blur * 0.25, 0.38);

    float body = smoothstep(0.02, 0.76, density);
    float deep = smoothstep(0.36, 1.0, density);
    float rim = clamp((blur * 0.25 - density) * 1.05 + speed * 0.04, 0.0, 1.0);
    float rimInner = smoothstep(0.06, 0.4, rim) * (1.0 - smoothstep(0.5, 0.95, rim));
    float rimOuter = smoothstep(0.28, 0.92, rim);
    float contourNoise = (sin(vUv.x * 9.0 + density * 6.0) * sin(vUv.y * 8.0 - density * 5.0)) * 0.5 + 0.5;
    float contourShade = rimOuter * (0.9 + 0.1 * contourNoise);
    float mist = smoothstep(0.0, 0.55, density) * 0.28;
    float haze = smoothstep(0.0, 0.3, density) * 0.18;

    // Time-evolving grayscale palette for a living, chroma-like motion in monochrome.
    float pulseA = 0.5 + 0.5 * sin(uTime * 0.32 + vUv.x * 2.6);
    float pulseB = 0.5 + 0.5 * sin(uTime * 0.21 + vUv.y * 2.2 + 1.6);
    float toneShift = (pulseA * 0.65 + pulseB * 0.35 - 0.5);
    float dynamicMid = clamp(uMidGray + toneShift * 0.028, 0.0, 1.0);
    float dynamicInk = clamp(uInkGray - toneShift * 0.038, 0.0, 1.0);

    float breath = sin(uPresetBreath * 3.14159265);
    float g = mix(uBaseGray, dynamicMid, body);
    g = mix(g, dynamicInk, deep * 0.4);
    g = mix(g, uBaseGray, mist * 0.3 + haze * 0.17);
    g = mix(g, dynamicInk, contourShade * 0.15);
    g = mix(g, uBaseGray, rimInner * 0.22 + rimOuter * 0.25);

    // Relief shading from fluid gradient.
    vec3 normal = normalize(vec3((dL - dR) * 8.0, (dB - dT) * 8.0, 1.0));
    vec3 lightDir = normalize(vec3(-0.55 + 0.12 * sin(uTime * 0.25), 0.75, 0.85));
    float diffuse = max(dot(normal, lightDir), 0.0);
    float specular = pow(max(dot(reflect(-lightDir, normal), vec3(0.0, 0.0, 1.0)), 0.0), 18.0);
    float relief = (diffuse - 0.55) * 0.2 + specular * 0.09;
    g = clamp(g + relief * (0.35 + deep * 0.45 + breath * 0.2), 0.0, 1.0);

    // Alpha gives depth while keeping background breathing.
    float alpha = clamp(0.18 * body + 0.31 * deep + 0.12 * rimInner + 0.15 * rimOuter + 0.12 * mist + breath * 0.09, 0.0, 0.62);
    vec3 color = mix(vec3(uBaseGray), vec3(g), alpha);
    gl_FragColor = vec4(color, 1.0);
  }
`;

const createTarget = (width: number, height: number) =>
  new WebGLRenderTarget(width, height, {
    type: HalfFloatType,
    format: RGBAFormat,
    minFilter: LinearFilter,
    magFilter: LinearFilter,
    depthBuffer: false,
    stencilBuffer: false,
    wrapS: ClampToEdgeWrapping,
    wrapT: ClampToEdgeWrapping,
  });

const createDoubleFBO = (width: number, height: number): DoubleFBO => {
  const fboA = createTarget(width, height);
  const fboB = createTarget(width, height);
  return {
    read: fboA,
    write: fboB,
    swap() {
      const temp = this.read;
      this.read = this.write;
      this.write = temp;
    },
  };
};

interface InteractiveFluidBackgroundProps {
  settings?: FluidSettings;
  presetTransitionProgress?: number;
}

export const InteractiveFluidBackground: React.FC<InteractiveFluidBackgroundProps> = ({
  settings = DEFAULT_FLUID_SETTINGS,
  presetTransitionProgress = 0,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const settingsRef = useRef<FluidSettings>(settings);
  const transitionProgressRef = useRef<number>(presetTransitionProgress);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  useEffect(() => {
    transitionProgressRef.current = presetTransitionProgress;
  }, [presetTransitionProgress]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const renderer = new WebGLRenderer({
      canvas,
      antialias: false,
      alpha: false,
      powerPreference: 'high-performance',
    });
    renderer.autoClear = false;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

    const scene = new Scene();
    const camera: Camera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const quad = new Mesh(new PlaneGeometry(2, 2));
    scene.add(quad);

    const pointer: PointerState = {
      x: 0,
      y: 0,
      dx: 0,
      dy: 0,
      sdx: 0,
      sdy: 0,
      moved: false,
      active: false,
      pointerType: 'mouse',
      pointerId: null,
    };
    const texelSize = new Vector2(1, 1);
    const fluidRes = { width: 1, height: 1 };
    let velocity = createDoubleFBO(1, 1);
    let dye = createDoubleFBO(1, 1);
    let pressure = createDoubleFBO(1, 1);
    let divergence = createTarget(1, 1);
    let curl = createTarget(1, 1);
    let lastTime = performance.now();

    const advectionMaterial = new ShaderMaterial({
      vertexShader: baseVertexShader,
      fragmentShader: advectionShader,
      uniforms: {
        uSource: { value: null },
        uVelocity: { value: null },
        uTexelSize: { value: texelSize },
        uDt: { value: 0.016 },
        uDissipation: { value: 0.98 },
      },
      depthTest: false,
      depthWrite: false,
    });

    const splatMaterial = new ShaderMaterial({
      vertexShader: baseVertexShader,
      fragmentShader: splatShader,
      uniforms: {
        uTarget: { value: null },
        uPoint: { value: new Vector2(0.5, 0.5) },
        uColor: { value: new Vector3(0.2, 0.2, 0.2) },
        uForce: { value: new Vector2(0, 0) },
        uRadius: { value: 0.03 },
        uIsVelocity: { value: 0 },
        uAspect: { value: 1 },
      },
      transparent: false,
      depthTest: false,
      depthWrite: false,
    });

    const divergenceMaterial = new ShaderMaterial({
      vertexShader: baseVertexShader,
      fragmentShader: divergenceShader,
      uniforms: {
        uVelocity: { value: null },
        uTexelSize: { value: texelSize },
      },
      depthTest: false,
      depthWrite: false,
    });

    const pressureMaterial = new ShaderMaterial({
      vertexShader: baseVertexShader,
      fragmentShader: pressureShader,
      uniforms: {
        uPressure: { value: null },
        uDivergence: { value: null },
        uTexelSize: { value: texelSize },
      },
      depthTest: false,
      depthWrite: false,
    });

    const viscosityMaterial = new ShaderMaterial({
      vertexShader: baseVertexShader,
      fragmentShader: viscosityShader,
      uniforms: {
        uVelocity: { value: null },
        uTexelSize: { value: texelSize },
        uAlpha: { value: 0 },
        uBeta: { value: 1 },
      },
      depthTest: false,
      depthWrite: false,
    });

    const gradientMaterial = new ShaderMaterial({
      vertexShader: baseVertexShader,
      fragmentShader: gradientSubtractShader,
      uniforms: {
        uPressure: { value: null },
        uVelocity: { value: null },
        uTexelSize: { value: texelSize },
      },
      depthTest: false,
      depthWrite: false,
    });

    const curlMaterial = new ShaderMaterial({
      vertexShader: baseVertexShader,
      fragmentShader: curlShader,
      uniforms: {
        uVelocity: { value: null },
        uTexelSize: { value: texelSize },
      },
      depthTest: false,
      depthWrite: false,
    });

    const vorticityMaterial = new ShaderMaterial({
      vertexShader: baseVertexShader,
      fragmentShader: vorticityShader,
      uniforms: {
        uVelocity: { value: null },
        uCurl: { value: null },
        uTexelSize: { value: texelSize },
        uDt: { value: 0.016 },
        uCurlStrength: { value: 22.0 },
      },
      depthTest: false,
      depthWrite: false,
    });

    const displayMaterial = new ShaderMaterial({
      vertexShader: baseVertexShader,
      fragmentShader: displayShader,
      uniforms: {
        uDye: { value: null },
        uVelocity: { value: null },
        uBaseGray: { value: BASE_GRAY },
        uMidGray: { value: MID_GRAY },
        uInkGray: { value: INK_GRAY },
        uTexelSize: { value: texelSize },
        uTime: { value: 0 },
        uPresetBreath: { value: 0 },
      },
      depthTest: false,
      depthWrite: false,
    });

    const renderPass = (material: ShaderMaterial, target: WebGLRenderTarget | null) => {
      quad.material = material;
      renderer.setRenderTarget(target);
      renderer.render(scene, camera);
    };

    const disposeFBO = (fbo: DoubleFBO) => {
      fbo.read.dispose();
      fbo.write.dispose();
    };

    const resize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      renderer.setSize(width, height, false);

      const simScale = Math.min(1, 1024 / Math.max(width, height));
      fluidRes.width = Math.max(180, Math.floor(width * simScale));
      fluidRes.height = Math.max(120, Math.floor(height * simScale));
      texelSize.set(1 / fluidRes.width, 1 / fluidRes.height);

      disposeFBO(velocity);
      disposeFBO(dye);
      disposeFBO(pressure);
      divergence.dispose();
      curl.dispose();
      velocity = createDoubleFBO(fluidRes.width, fluidRes.height);
      dye = createDoubleFBO(fluidRes.width, fluidRes.height);
      pressure = createDoubleFBO(fluidRes.width, fluidRes.height);
      divergence = createTarget(fluidRes.width, fluidRes.height);
      curl = createTarget(fluidRes.width, fluidRes.height);
    };

    const splat = (
      x: number,
      y: number,
      dx: number,
      dy: number,
      options?: { radiusBoost?: number; tintBoost?: number; forceBoost?: number }
    ) => {
      const cfg = settingsRef.current;
      const isTouchLike = pointer.pointerType === 'touch' || pointer.pointerType === 'pen';
      const nx = x / window.innerWidth;
      const ny = 1 - y / window.innerHeight;
      const speed = Math.min(6.5, Math.hypot(dx, dy) * 0.075);
      const aspect = window.innerWidth / Math.max(window.innerHeight, 1);
      const minSide = Math.max(1, Math.min(window.innerWidth, window.innerHeight));
      const radiusNorm = cfg.cursorSize / minSide;
      const radiusBoost = options?.radiusBoost ?? 1;
      const tintBoost = options?.tintBoost ?? 1;
      const forceBoost = options?.forceBoost ?? 1;
      const touchRadiusBoost = isTouchLike ? 0.24 : 1;
      const touchForceBoost = isTouchLike ? 1.18 : 1;
      const radius = Math.max(
        0.00006,
        radiusNorm * radiusNorm * (0.9 + speed * 0.28) * radiusBoost * touchRadiusBoost
      );
      const forceScale = cfg.mouseForce * 0.0022 * forceBoost * touchForceBoost;

      splatMaterial.uniforms.uPoint.value.set(nx, ny);
      splatMaterial.uniforms.uAspect.value = aspect;
      splatMaterial.uniforms.uRadius.value = radius;

      splatMaterial.uniforms.uTarget.value = velocity.read.texture;
      splatMaterial.uniforms.uForce.value.set(dx * forceScale, -dy * forceScale);
      splatMaterial.uniforms.uIsVelocity.value = 1;
      renderPass(splatMaterial, velocity.write);
      velocity.swap();

      const tint = (0.03 + speed * 0.026) * tintBoost;
      splatMaterial.uniforms.uTarget.value = dye.read.texture;
      splatMaterial.uniforms.uColor.value.set(tint, tint, tint);
      splatMaterial.uniforms.uForce.value.set(0, 0);
      splatMaterial.uniforms.uIsVelocity.value = 0;
      renderPass(splatMaterial, dye.write);
      dye.swap();
    };

    const advect = (
      target: DoubleFBO,
      sourceTexture: WebGLRenderTarget['texture'],
      velocityTexture: WebGLRenderTarget['texture'],
      dt: number,
      dissipation: number
    ) => {
      advectionMaterial.uniforms.uSource.value = sourceTexture;
      advectionMaterial.uniforms.uVelocity.value = velocityTexture;
      advectionMaterial.uniforms.uDt.value = dt;
      advectionMaterial.uniforms.uDissipation.value = dissipation;
      renderPass(advectionMaterial, target.write);
      target.swap();
    };

    const onPointerMove = (event: PointerEvent) => {
      const eventType = (event.pointerType as 'mouse' | 'touch' | 'pen') || 'mouse';

      if (
        pointer.pointerId !== null &&
        pointer.pointerType !== 'mouse' &&
        event.pointerId !== pointer.pointerId
      ) {
        return;
      }

      // On touch/pen, react only while the active contact is down.
      if ((eventType === 'touch' || eventType === 'pen') && !pointer.active) {
        return;
      }

      if (!pointer.active) {
        pointer.x = event.clientX;
        pointer.y = event.clientY;
        pointer.dx = 0;
        pointer.dy = 0;
        pointer.sdx = 0;
        pointer.sdy = 0;
        pointer.active = true;
        pointer.pointerType = eventType;
        pointer.pointerId = event.pointerId;
        return;
      }
      pointer.dx = event.clientX - pointer.x;
      pointer.dy = event.clientY - pointer.y;
      pointer.x = event.clientX;
      pointer.y = event.clientY;
      pointer.sdx = pointer.sdx * 0.72 + pointer.dx * 0.28;
      pointer.sdy = pointer.sdy * 0.72 + pointer.dy * 0.28;
      pointer.pointerType = eventType;
      pointer.moved = true;
    };

    const onPointerDown = (event: PointerEvent) => {
      pointer.pointerType = (event.pointerType as 'mouse' | 'touch' | 'pen') || 'mouse';
      pointer.pointerId = event.pointerId;
      pointer.active = true;
      pointer.x = event.clientX;
      pointer.y = event.clientY;
      pointer.dx = 0;
      pointer.dy = 0;
      pointer.sdx = 0;
      pointer.sdy = 0;
      pointer.moved = true;
    };

    const onPointerEnd = () => {
      pointer.active = false;
      pointer.dx = 0;
      pointer.dy = 0;
      pointer.sdx = 0;
      pointer.sdy = 0;
      pointer.pointerId = null;
    };

    const tick = () => {
      const cfg = settingsRef.current;
      const now = performance.now();
      const dt = Math.min((now - lastTime) / 1000, 0.033);
      lastTime = now;

      if (cfg.isViscous) {
        const alpha = (fluidRes.width * fluidRes.height) / Math.max(cfg.viscous, 1);
        viscosityMaterial.uniforms.uAlpha.value = alpha;
        viscosityMaterial.uniforms.uBeta.value = 4.0 + alpha;
        for (let i = 0; i < cfg.iterationsViscous; i += 1) {
          viscosityMaterial.uniforms.uVelocity.value = velocity.read.texture;
          renderPass(viscosityMaterial, velocity.write);
          velocity.swap();
        }
      }

      curlMaterial.uniforms.uVelocity.value = velocity.read.texture;
      renderPass(curlMaterial, curl);

      vorticityMaterial.uniforms.uVelocity.value = velocity.read.texture;
      vorticityMaterial.uniforms.uCurl.value = curl.texture;
      vorticityMaterial.uniforms.uDt.value = dt;
      renderPass(vorticityMaterial, velocity.write);
      velocity.swap();

      divergenceMaterial.uniforms.uVelocity.value = velocity.read.texture;
      renderPass(divergenceMaterial, divergence);

      pressureMaterial.uniforms.uDivergence.value = divergence.texture;
      for (let i = 0; i < cfg.iterationsPoisson; i += 1) {
        pressureMaterial.uniforms.uPressure.value = pressure.read.texture;
        renderPass(pressureMaterial, pressure.write);
        pressure.swap();
      }

      gradientMaterial.uniforms.uPressure.value = pressure.read.texture;
      gradientMaterial.uniforms.uVelocity.value = velocity.read.texture;
      renderPass(gradientMaterial, velocity.write);
      velocity.swap();

      const simDt = Math.min(dt, cfg.dt);
      advect(
        velocity,
        velocity.read.texture,
        velocity.read.texture,
        simDt * 21,
        cfg.velocityDissipation
      );
      advect(
        dye,
        dye.read.texture,
        velocity.read.texture,
        simDt * 23,
        cfg.dyeDissipation
      );

      if (pointer.moved) {
        const speed = Math.hypot(pointer.sdx, pointer.sdy);
        const splatCount = Math.min(8, Math.max(2, Math.floor(speed / 6)));
        for (let i = 0; i < splatCount; i += 1) {
          const t = i / splatCount;
          const px = pointer.x - pointer.sdx * (1 - t);
          const py = pointer.y - pointer.sdy * (1 - t);
          splat(px, py, pointer.sdx, pointer.sdy);
        }
        pointer.moved = false;
      }

      const breathProgress = transitionProgressRef.current;
      if (breathProgress > 0.001 && breathProgress < 0.999) {
        const breath = Math.sin(breathProgress * Math.PI);
        const w = window.innerWidth;
        const h = window.innerHeight;
        const bx = w * (0.5 + Math.sin(now * 0.0009) * 0.05);
        const by = h * (0.52 + Math.cos(now * 0.0008) * 0.04);

        splat(bx, by, 0.9 * breath, -0.7 * breath, {
          radiusBoost: 2.2 + breath * 0.9,
          tintBoost: 1.15 + breath * 0.35,
          forceBoost: 0.35 + breath * 0.2,
        });
      }

      displayMaterial.uniforms.uDye.value = dye.read.texture;
      displayMaterial.uniforms.uVelocity.value = velocity.read.texture;
      displayMaterial.uniforms.uTime.value = now * 0.001;
      displayMaterial.uniforms.uPresetBreath.value = transitionProgressRef.current;
      renderPass(displayMaterial, null);
      rafRef.current = window.requestAnimationFrame(tick);
    };

    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('pointerup', onPointerEnd, { passive: true });
    window.addEventListener('pointercancel', onPointerEnd, { passive: true });
    window.addEventListener('blur', onPointerEnd);
    rafRef.current = window.requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerEnd);
      window.removeEventListener('pointercancel', onPointerEnd);
      window.removeEventListener('blur', onPointerEnd);
      if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current);

      disposeFBO(velocity);
      disposeFBO(dye);
      disposeFBO(pressure);
      divergence.dispose();
      curl.dispose();
      advectionMaterial.dispose();
      splatMaterial.dispose();
      divergenceMaterial.dispose();
      pressureMaterial.dispose();
      viscosityMaterial.dispose();
      gradientMaterial.dispose();
      curlMaterial.dispose();
      vorticityMaterial.dispose();
      displayMaterial.dispose();
      quad.geometry.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0 h-full w-full touch-none"
      aria-hidden="true"
    />
  );
};
